import apiRoutes from '../constants/apiRoutes';
import baseApi from '../services/api';
import { PaginatedResponse } from './types/common';

export interface ReferrerBankPayload {
  bankName: string;
  bankAddress: string;
  swiftCode: string;
  ibanCode: string;
  accountNumber: string;
  routingNumber: string;
}

export interface CreateReferrerPayload {
  companyId: string;
  userId: string | null;
  userName: string;
  email: string;
  commissionPercentage: number;
  type: string;
  expiryDate: string | null;
  numberOfCohorts: number | null;
}

export interface UpdateReferrerPayload {
  referrerId: string;
  commissionPercentage: number;
  type: string;
  expiryDate: string | null;
  numberOfCohorts: number | null;
}

export enum ReferrerType {
  PERPETUAL = 'PERPETUAL',
  COHORT_BASED = 'COHORT_BASED',
}

export enum ReferrerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface ReferrerDetails {
  referrerId: string;
  type: ReferrerType;
  status: ReferrerStatus;
  email: string;
  numberOfCohorts: number | null;
  commissionPercentage: string;
}

export interface ReferrerResponse {
  data: ReferrerDetails[];
  code: number;
  message: string;
}

export interface AddReferrerPayload {
  gameId: string;
  referrerDealMappingDetails: (Omit<ReferrerDetails, 'status' | 'commissionPercentage'> & {
    commissionPercentage: number;
  })[];
}
const referrers = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReferrersPaginated: builder.infiniteQuery<
      PaginatedResponse<ReferrerDetails>,
      string,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.data.totalPages === lastPageParam + 1) return undefined;
          return lastPageParam + 1;
        },
      },

      query: ({ queryArg, pageParam }) => {
        const base = `${apiRoutes.referrers}?`;
        return `${base}${queryArg || ''}&pageNumber=${pageParam}`;
      },
    }),
    getReferrers: builder.query<any, any>({
      query: ({ search, pageNumber, pageSize }) => {
        let baseEndpoint = `${apiRoutes.referrers}?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (search) {
          baseEndpoint += `&search=${search}`;
        }

        return baseEndpoint;
      },
    }),
    getReferrerById: builder.query<any, any>({
      query: ({ referrerId }) => `${apiRoutes.referrers}/${referrerId}`,
    }),
    createReferrer: builder.mutation<any, CreateReferrerPayload>({
      query: (payload) => ({
        url: apiRoutes.referrers,
        method: 'POST',
        body: payload,
      }),
    }),
    updateReferrer: builder.mutation<any, UpdateReferrerPayload>({
      query: (payload) => ({
        url: apiRoutes.referrers,
        method: 'PUT',
        body: payload,
      }),
    }),
    mapReferrersToDeal: builder.mutation<any, AddReferrerPayload>({
      query: (payload) => ({
        url: `${apiRoutes.referrers}/deal-mapping`,
        method: 'POST',
        body: payload,
      }),
    }),
    getReferrersByGameId: builder.query<ReferrerResponse, string>({
      query: (gameId) => `${apiRoutes.referrersByGameId(gameId)}`,
    }),
    getReferrerBank: builder.query<any, { referrerId: string }>({
      query: ({ referrerId }) => `${apiRoutes.referrers}/${referrerId}/banks`,
    }),
    createReferrerBank: builder.mutation<
      any,
      { referrerId: string; referrerDetails: ReferrerBankPayload }
    >({
      query: (payload) => ({
        url: `${apiRoutes.referrers}/${payload.referrerId}/banks`,
        method: 'POST',
        body: payload.referrerDetails,
      }),
    }),
  }),
});

export const {
  useGetReferrersPaginatedInfiniteQuery,
  useLazyGetReferrersQuery,
  useLazyGetReferrerByIdQuery,
  useCreateReferrerMutation,
  useUpdateReferrerMutation,
  useMapReferrersToDealMutation,
  useLazyGetReferrersByGameIdQuery,
  useLazyGetReferrerBankQuery,
  useCreateReferrerBankMutation,
} = referrers;
